import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Boxes, CheckSquare, Loader2, Save, ShieldCheck, XCircle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/efs/PageHeader';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';
import { cn } from '@/lib/utils';

import { ApplyWorkPackageDialog } from '@/features/settings/work-packages/ApplyWorkPackageDialog';

import { usePermissionsList, useRolesList, useUpdateRole } from './useRoles';
import type { PermissionDto, RoleDto } from './types';

const NS = 'settings';

interface PermissionGroup {
  category: string;
  rows: PermissionDto[];
}

const sortedPermissionKey = (permissions: string[]) => [...permissions].sort().join('|');

export const RoleEditPage = () => {
  const { t } = useTranslation(NS);
  const { roleCode = '' } = useParams();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const rolesQuery = useRolesList();
  const permissionsQuery = usePermissionsList();
  const updateRole = useUpdateRole();
  const roles = rolesQuery.data ?? [];
  const role = roles.find((row) => row.role_code === roleCode);
  const [draftPermissions, setDraftPermissions] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [applyOpen, setApplyOpen] = useState(false);
  const canApplyWorkPackage =
    hasPermission(user, 'workpackage.assign') && hasPermission(user, 'role.manage');

  const groups = useMemo<PermissionGroup[]>(() => {
    const map = new Map<string, PermissionDto[]>();
    (permissionsQuery.data ?? [])
      .filter((permission) => permission.active_flag && permission.grantable_flag)
      .forEach((permission) => {
        const rows = map.get(permission.category) ?? [];
        rows.push(permission);
        map.set(permission.category, rows);
      });

    return Array.from(map.entries()).map(([category, rows]) => ({
      category,
      rows: rows.sort((a, b) => a.sort_order - b.sort_order),
    }));
  }, [permissionsQuery.data]);

  useEffect(() => {
    if (role) setDraftPermissions(role.permissions);
  }, [role]);

  useEffect(() => {
    if (!activeCategory && groups[0]) setActiveCategory(groups[0].category);
  }, [activeCategory, groups]);

  const activeGroup = groups.find((group) => group.category === activeCategory) ?? groups[0];
  const isSystemAdminRole = role?.role_code === 'system_admin';
  const effectivePermissions = isSystemAdminRole
    ? (permissionsQuery.data ?? []).map((permission) => permission.permission_code)
    : draftPermissions;
  const dirty = role
    ? sortedPermissionKey(draftPermissions) !== sortedPermissionKey(role.permissions)
    : false;

  const roleLabel = (row: RoleDto) =>
    t(`efs.settings.users.roles.${row.role_code}`, {
      defaultValue: row.display_name || row.role_code,
    });
  const permissionLabel = (permission: PermissionDto) =>
    t(`efs.settings.roles.permissions.${permission.permission_code}`, {
      defaultValue: permission.display_name || permission.permission_code,
    });
  const permissionDescription = (permission: PermissionDto) =>
    t(`efs.settings.roles.permission_descriptions.${permission.permission_code}`, {
      defaultValue: permission.description || '',
    });
  const categoryLabel = (category: string) =>
    t(`efs.settings.roles.categories.${category}`, { defaultValue: category });
  const categoryDescription = (category: string) =>
    t(`efs.settings.roles.category_descriptions.${category}`, {
      defaultValue: t('efs.settings.roles.category_default_description'),
    });

  const togglePermission = (permissionCode: string, checked: boolean) => {
    setDraftPermissions((current) => {
      if (checked) return Array.from(new Set([...current, permissionCode])).sort();
      return current.filter((permission) => permission !== permissionCode);
    });
  };

  const setCategoryPermissions = (checked: boolean) => {
    if (!activeGroup) return;
    const categoryCodes = activeGroup.rows.map((permission) => permission.permission_code);
    setDraftPermissions((current) => {
      if (checked) return Array.from(new Set([...current, ...categoryCodes])).sort();
      return current.filter((permission) => !categoryCodes.includes(permission));
    });
  };

  const save = async () => {
    if (!role) return;
    await updateRole.mutateAsync({
      roleCode: role.role_code,
      body: { permissions: draftPermissions },
    });
    navigate('/settings/roles');
  };

  const selectedRows = (activeGroup?.rows ?? []).filter((permission) =>
    effectivePermissions.includes(permission.permission_code),
  );
  const categorySelectedCount = (group: PermissionGroup) =>
    group.rows.filter((permission) => effectivePermissions.includes(permission.permission_code))
      .length;

  if (rolesQuery.isLoading || permissionsQuery.isLoading) {
    return (
      <div className="py-12 text-center text-sm text-efs-text-muted">
        {t('efs.common.messages.loading', { ns: 'common' })}
      </div>
    );
  }

  if (!role) {
    return (
      <div className="space-y-4">
        <PageHeader
          title={t('efs.settings.roles.not_found_title')}
          subtitle={t('efs.settings.roles.not_found_subtitle', { roleCode })}
        />
        <Button asChild variant="outline">
          <Link to="/settings/roles">
            <ArrowLeft className="h-4 w-4" />
            {t('efs.settings.roles.actions.back_to_list')}
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.settings.roles.edit_title', { role: roleLabel(role) })}
        subtitle={t('efs.settings.roles.edit_subtitle')}
        actions={
          <>
            <Button asChild variant="outline">
              <Link to="/settings/roles">
                <ArrowLeft className="h-4 w-4" />
                {t('efs.settings.roles.actions.back_to_list')}
              </Link>
            </Button>
            {canApplyWorkPackage && !isSystemAdminRole ? (
              <Button variant="outline" onClick={() => setApplyOpen(true)}>
                <Boxes className="h-4 w-4" />
                {t('efs.settings.work_packages.apply.action')}
              </Button>
            ) : null}
            <Button
              onClick={() => void save()}
              disabled={isSystemAdminRole || !dirty || updateRole.isPending}
            >
              {updateRole.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {t('efs.settings.roles.actions.save_permissions')}
            </Button>
          </>
        }
      />

      <section className="rounded-md border border-efs-border bg-efs-bg-panel px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-semibold text-efs-text-primary">{roleLabel(role)}</h2>
              {role.system_role ? (
                <Badge variant="secondary">{t('efs.settings.roles.system_role')}</Badge>
              ) : null}
              {role.active_flag ? null : (
                <Badge variant="outline">{t('efs.settings.roles.inactive')}</Badge>
              )}
            </div>
            <p className="mt-1 break-all text-xs text-efs-text-muted">{role.role_code}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-efs-text-muted">
            <span className="rounded-md border border-efs-border px-2 py-1">
              {t('efs.settings.roles.summary.permissions', {
                count: effectivePermissions.length,
              })}
            </span>
            {role.party_type ? (
              <span className="rounded-md border border-efs-border px-2 py-1">
                {t(`efs.settings.companies.types.${role.party_type}`, {
                  defaultValue: role.party_type,
                })}
              </span>
            ) : null}
          </div>
        </div>
        {isSystemAdminRole ? (
          <p className="mt-3 rounded-md bg-efs-bg-subtle px-3 py-2 text-xs text-efs-text-muted">
            {t('efs.settings.roles.system_admin_edit_note')}
          </p>
        ) : null}
      </section>

      <div className="grid gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <nav className="rounded-md border border-efs-border bg-efs-bg-panel p-2">
          <div className="px-2 pb-2 text-xs font-semibold text-efs-text-muted">
            {t('efs.settings.roles.feature_menu')}
          </div>
          <div className="space-y-1">
            {groups.map((group) => {
              const selected = categorySelectedCount(group);
              return (
                <button
                  key={group.category}
                  type="button"
                  onClick={() => setActiveCategory(group.category)}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 rounded-md px-3 py-2 text-left text-sm hover:bg-efs-bg-subtle',
                    activeGroup?.category === group.category && 'bg-efs-bg-subtle font-medium',
                  )}
                >
                  <span className="truncate">{categoryLabel(group.category)}</span>
                  <span className="shrink-0 text-xs text-efs-text-muted">
                    {selected}/{group.rows.length}
                  </span>
                </button>
              );
            })}
          </div>
        </nav>

        <section className="min-w-0 space-y-3">
          <div className="rounded-md border border-efs-border bg-efs-bg-panel px-4 py-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-efs-text-primary">
                  {activeGroup ? categoryLabel(activeGroup.category) : '-'}
                </h3>
                <p className="mt-1 text-sm text-efs-text-muted">
                  {activeGroup ? categoryDescription(activeGroup.category) : ''}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={isSystemAdminRole || !activeGroup}
                  onClick={() => setCategoryPermissions(true)}
                >
                  <CheckSquare className="h-4 w-4" />
                  {t('efs.settings.roles.actions.select_feature_all')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={isSystemAdminRole || !activeGroup}
                  onClick={() => setCategoryPermissions(false)}
                >
                  <XCircle className="h-4 w-4" />
                  {t('efs.settings.roles.actions.clear_feature_all')}
                </Button>
              </div>
            </div>
          </div>

          <div className="rounded-md border border-efs-border bg-efs-bg-panel">
            <div className="grid grid-cols-[4rem_minmax(12rem,1fr)_9rem] border-b border-efs-border bg-efs-bg-subtle px-3 py-2 text-xs font-semibold text-efs-text-muted">
              <span>{t('efs.settings.roles.fields.enabled')}</span>
              <span>{t('efs.settings.roles.fields.permission')}</span>
              <span>{t('efs.settings.roles.fields.action')}</span>
            </div>
            <div className="divide-y divide-efs-border">
              {(activeGroup?.rows ?? []).map((permission) => {
                const checked = effectivePermissions.includes(permission.permission_code);
                return (
                  <label
                    key={permission.permission_code}
                    className="grid cursor-pointer grid-cols-[4rem_minmax(12rem,1fr)_9rem] gap-2 px-3 py-3 hover:bg-efs-bg-subtle"
                  >
                    <span>
                      <input
                        type="checkbox"
                        className="h-4 w-4"
                        checked={checked}
                        disabled={isSystemAdminRole}
                        onChange={(event) =>
                          togglePermission(permission.permission_code, event.target.checked)
                        }
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-medium text-efs-text-primary">
                        {permissionLabel(permission)}
                      </span>
                      <span className="mt-1 block text-xs text-efs-text-muted">
                        {permissionDescription(permission) || permission.permission_code}
                      </span>
                    </span>
                    <span className="text-xs text-efs-text-muted">
                      <span className="rounded-md border border-efs-border px-2 py-1">
                        {permission.action}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <section className="rounded-md border border-efs-border bg-efs-bg-panel px-4 py-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-efs-text-primary">
              <ShieldCheck className="h-4 w-4" />
              {t('efs.settings.roles.allowed_actions_title')}
            </div>
            {selectedRows.length > 0 ? (
              <ul className="mt-3 grid gap-2 md:grid-cols-2">
                {selectedRows.map((permission) => (
                  <li
                    key={permission.permission_code}
                    className="rounded-md border border-efs-border px-3 py-2"
                  >
                    <div className="text-sm font-medium text-efs-text-primary">
                      {permissionLabel(permission)}
                    </div>
                    <div className="mt-1 text-xs text-efs-text-muted">
                      {permissionDescription(permission) || permission.permission_code}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-efs-text-muted">
                {t('efs.settings.roles.no_allowed_actions')}
              </p>
            )}
          </section>
        </section>
      </div>

      {canApplyWorkPackage && !isSystemAdminRole ? (
        <ApplyWorkPackageDialog
          open={applyOpen}
          onOpenChange={setApplyOpen}
          roleCode={role.role_code}
          partyType={role.party_type}
        />
      ) : null}
    </div>
  );
};

export default RoleEditPage;
